import React, { forwardRef, useRef, useImperativeHandle, useEffect, useMemo } from "react";
import { Container } from "@pixi/react";
import { useDispatch, useSelector } from "react-redux";
import { reportOverlayReelReady } from "../../State/actions";
import { selectHoldAndWinSymbolsReady } from "../../State/selectors";
import { Settings } from "../../settings";
import HoldAndWinOverlaySymbol from "./HoldAndWinOverlaySymbol";

/**
 * HoldAndWinOverlayReel - Container for overlay symbols in Hold & Win
 * Contains 4 overlay symbols (one per symbol position)
 * No reel controller - each symbol has its own controller
 */
const HoldAndWinOverlayReel = forwardRef(
  ({ index, symbolWidth, symbolHeight, reelSpacing }, ref) => {
    const overlaySymbolRefs = useRef(new Array(Settings.SYMBOLS_VISIBLE));
    const reportedReadyRef = useRef(false);
    const dispatch = useDispatch();
    const holdAndWinSymbolsReady = useSelector(selectHoldAndWinSymbolsReady);

    // Use divider width for vertical spacing with 1.3x multiplier (matches HoldAndWinReel)
    // verticalSpacing calculated but not used - kept for future reference

    // Expose method to get symbol ref by index
    useImperativeHandle(ref, () => ({
      getSymbolRef: (symbolIndex) => overlaySymbolRefs.current[symbolIndex],
    }));

    // Report to Redux when this overlay reel's refs are ready
    useEffect(() => {
      if (!holdAndWinSymbolsReady || reportedReadyRef.current) {
        return;
      }

      // Check if all overlay symbol refs are populated
      const allRefsPopulated = overlaySymbolRefs.current.length === Settings.SYMBOLS_VISIBLE &&
        overlaySymbolRefs.current.every(ref => ref);

      if (allRefsPopulated) {
        dispatch(reportOverlayReelReady());
        reportedReadyRef.current = true;
      }
    }, [holdAndWinSymbolsReady, index, dispatch]);

    // Create 4 overlay symbols (one for each position) - memoized
    const overlaySymbols = useMemo(
      () => Array.from({ length: Settings.SYMBOLS_VISIBLE }, (_, idx) => idx),
      []
    );

    // Memoize container X position
    const containerX = useMemo(
      () => reelSpacing * (index - 2),
      [reelSpacing, index]
    );

    return (
      <Container
        name={`hold_and_win_overlay_reel_${index}`}
        x={containerX}
      >
        {overlaySymbols.map((_, idx) => (
          <HoldAndWinOverlaySymbol
            key={idx}
            index={idx}
            reelIndex={index}
            y={symbolHeight * idx + idx}
            symbolWidth={symbolWidth}
            symbolHeight={symbolHeight}
            ref={(el) => {
              overlaySymbolRefs.current[idx] = el;
            }}
          />
        ))}
      </Container>
    );
  }
);

export default HoldAndWinOverlayReel;
